//src/routes/_layout/chatList.tsx
import {
  Box,
  VStack,
  Text,
  Button,
  Flex,
  Input,
  HStack,
  Checkbox,
} from "@chakra-ui/react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { ChatService } from "@/client";
import useCustomToast from "@/hooks/useCustomToast";
import { usePendingMessage } from "@/context/PendingMessageContext";
import { FiSearch, FiPlus } from "react-icons/fi";
import DeleteConversation from "@/components/Chats/DeleteConversation";

export const Route = createFileRoute("/_layout/chatList")({
  component: ChatList,
});

interface Conversation {
  id: string;
  conv_title: string;
  updated_at: string;
}

function ChatList() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showErrorToast, showSuccessToast } = useCustomToast();
  const { pendingMessage, clearPendingMessage, setPendingMessage } =
    usePendingMessage();
  const [, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // 선택 모드 관련 상태
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedChats, setSelectedChats] = useState<string[]>([]);

  // 호버 상태 관리
  const [hoveredChatId, setHoveredChatId] = useState<string | null>(null);

  // 대화 목록 조회
  const { data: conversationsResponse, isLoading: isLoadingConversations } =
    useQuery({
      queryKey: ["conversations"],
      queryFn: async () => {
        try {
          const response = await ChatService.readConversations();
          return response as { data: Conversation[] };
        } catch (error) {
          console.error("Failed to fetch conversations:", error);
          return { data: [] };
        }
      },
    });

  const conversations = conversationsResponse?.data || [];

  // 검색된 대화 필터링 및 최신 순으로 정렬
  const filteredConversations = conversations
    .filter((conv: Conversation) =>
      conv.conv_title?.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort(
      (a, b) =>
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
    );

  // 새 대화 생성
  const createChatMutation = useMutation({
    mutationFn: async ({
      msg,
      options: _options,
    }: {
      msg: string;
      options?: Record<string, unknown>;
    }) => {
      setIsLoading(true);
      const chatResponse = await ChatService.createConversation({
        message: msg,
      });
      return { chatResponse };
    },
    onSuccess: async (response, variables) => {
      const { msg, options } = variables;
      const { chatResponse } = response;

      if (!chatResponse?.id) {
        showErrorToast("❌ Failed to create conversation");
        return;
      }

      const chatOptions = {
        ...options,
      };

      setPendingMessage({ msg, options: chatOptions });
      navigate({ to: `/chat/${chatResponse.id}` });
      await queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: (error) => {
      console.error("❌ Request failed:", error);
      showErrorToast("❌ 요청에 실패했습니다. 다시 시도해주세요.");
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  // 선택된 채팅 삭제
  const deleteChatsMutation = useMutation({
    mutationFn: async (chatIds: string[]) => {
      const deletePromises = chatIds.map((id) =>
        ChatService.deleteConversation({ id })
      );
      await Promise.all(deletePromises);
    },
    onSuccess: () => {
      showSuccessToast("✅ 선택된 채팅이 삭제되었습니다.");
      setSelectedChats([]);
      setIsSelectionMode(false);
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    onError: (error) => {
      console.error("❌ Failed to delete chats:", error);
      showErrorToast("❌ 채팅 삭제에 실패했습니다.");
    },
  });

  const handleChatClick = (conversationId: string) => {
    // 체크박스 클릭 시 항상 선택/해제
    const newSelectedChats = selectedChats.includes(conversationId)
      ? selectedChats.filter((id) => id !== conversationId)
      : [...selectedChats, conversationId];

    setSelectedChats(newSelectedChats);

    // 선택된 항목이 있으면 선택 모드로 전환
    if (newSelectedChats.length > 0 && !isSelectionMode) {
      setIsSelectionMode(true);
    }
  };

  const handleCardClick = (conversationId: string) => {
    // 카드 클릭 시 채팅으로 이동
    navigate({ to: `/chat/${conversationId}` });
  };

  const handleSelectAll = () => {
    if (selectedChats.length === filteredConversations.length) {
      // 모두 선택된 상태면 모두 해제
      setSelectedChats([]);
    } else {
      // 모두 선택
      setSelectedChats(filteredConversations.map((conv) => conv.id));
    }
  };

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setSelectedChats([]);
  };

  const handleDeleteSelected = () => {
    if (selectedChats.length === 0) return;
    deleteChatsMutation.mutate(selectedChats);
  };

  const formatLastMessageTime = (lastMessageTime?: string) => {
    if (!lastMessageTime) return "방금 전";

    const now = new Date();
    const lastMessage = new Date(lastMessageTime);
    const diffInDays = Math.floor(
      (now.getTime() - lastMessage.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (diffInDays === 0) return "오늘";
    if (diffInDays === 1) return "1일 전";
    return `${diffInDays}일 전`;
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (pendingMessage) {
      if (pendingMessage.msg.trim()) {
        createChatMutation.mutate({
          msg: pendingMessage.msg,
          options: pendingMessage.options,
        });
      }
      clearPendingMessage();
    }
  }, [pendingMessage, createChatMutation.mutate, clearPendingMessage]);

  return (
    <Box w="100%" maxW="800px" mx="auto" px={6} py={8}>
      <VStack gap={6} w="100%" align="stretch">
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Text fontSize="2xl" fontWeight="700" color="gray.800">
            내 채팅 기록
          </Text>
          <Button
            data-testid="new-chat-button"
            variant="solid"
            maxW="150px"
            truncate
            onClick={() => navigate({ to: "/conversations" })}
          >
            <FiPlus fontSize="18px" />
            <Text>새 채팅</Text>
          </Button>
        </Flex>

        {/* Search Bar */}
        <Box position="relative">
          <Input
            placeholder="대화 내용 검색"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="gray.50"
            borderColor="gray.200"
            pl={10}
            _focus={{
              bg: "white",
              borderColor: "blue.400",
            }}
          />
          <Box
            position="absolute"
            left={3}
            top="50%"
            transform="translateY(-50%)"
            color="gray.400"
          >
            <FiSearch />
          </Box>
        </Box>

        {/* Selection Mode Header */}
        {isSelectionMode ? (
          <VStack gap={3} align="stretch">
            <Flex justify="space-between" align="center">
              <Text fontSize="sm" color="gray.600">
                선택된 채팅 {selectedChats.length}개
              </Text>
              <HStack gap={2}>
                <Button size="sm" variant="ghost" onClick={handleSelectAll}>
                  전체 선택
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleCancelSelection}
                >
                  취소
                </Button>
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={handleDeleteSelected}
                  disabled={
                    selectedChats.length === 0 || deleteChatsMutation.isPending
                  }
                  loading={deleteChatsMutation.isPending}
                >
                  선택한 항목 삭제
                </Button>
              </HStack>
            </Flex>
          </VStack>
        ) : (
          /* Info Message */
          <Text fontSize="sm" color="gray.600">
            {conversations.length}개의 채팅이 있습니다.{" "}
            <Text
              as="span"
              color="blue.500"
              cursor="pointer"
              onClick={() => setIsSelectionMode(true)}
            >
              선택
            </Text>
          </Text>
        )}

        <Box borderTop="1px solid" borderColor="gray.200" />

        {/* Chat List */}
        <VStack gap={3} align="stretch" maxH="60vh" overflowY="auto">
          {isLoadingConversations ? (
            <Text textAlign="center" color="gray.500" py={8}>
              로딩 중...
            </Text>
          ) : filteredConversations.length === 0 ? (
            <Text textAlign="center" color="gray.500" py={8}>
              {searchQuery ? "검색 결과가 없습니다." : "채팅 기록이 없습니다."}
            </Text>
          ) : (
            filteredConversations.map((conversation) => (
              <Flex key={conversation.id} align="center" gap={3}>
                {/* 체크박스 영역 */}
                <Box
                  onClick={(e) => {
                    e.stopPropagation();
                    handleChatClick(conversation.id);
                  }}
                >
                  <Checkbox.Root
                    checked={selectedChats.includes(conversation.id)}
                    size="sm"
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control
                      border="1px solid"
                      borderColor="blue.500"
                      _checked={{ bg: "blue.500" }}
                    >
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                  </Checkbox.Root>
                </Box>

                {/* 채팅 카드 영역 */}
                <Box
                  bg="gray.50"
                  borderRadius="lg"
                  p={4}
                  cursor="pointer"
                  border={
                    isSelectionMode && selectedChats.includes(conversation.id)
                      ? "2px solid"
                      : "1px solid"
                  }
                  borderColor={
                    isSelectionMode && selectedChats.includes(conversation.id)
                      ? "blue.400"
                      : "transparent"
                  }
                  _hover={{
                    bg: "gray.100",
                    transform: "translateY(-1px)",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
                  }}
                  transition="all 0.2s ease"
                  onClick={() => handleCardClick(conversation.id)}
                  flex={1}
                  onMouseEnter={() => setHoveredChatId(conversation.id)}
                  onMouseLeave={() => setHoveredChatId(null)}
                >
                  <Flex align="center" gap={3}>
                    <VStack align="stretch" gap={1} flex={1}>
                      <Text fontSize="md" fontWeight="600" color="gray.800">
                        {conversation.conv_title || "제목 없음"}
                      </Text>
                      <Text fontSize="sm" color="gray.500">
                        마지막 메시지{" "}
                        {formatLastMessageTime(conversation.updated_at)}
                      </Text>
                    </VStack>
                    {/* 호버 시에만 삭제 아이콘 표시 (선택 모드가 아닐 때만) */}
                    {!isSelectionMode && hoveredChatId === conversation.id && (
                      <DeleteConversation id={conversation.id} />
                    )}
                  </Flex>
                </Box>
              </Flex>
            ))
          )}
        </VStack>
      </VStack>
    </Box>
  );
}

export default ChatList;
